// A breach's casebook case or breach report: front matter as a grid, then the
// markdown body. Raw HTML in the markdown is never rendered.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { formatMetaValue } from '../lib/rollbackWatches';
import { getColors, rounded } from '../theme/tokens';
import { RollbackDocKind } from '../../types/DeploymentRecord';
import { mono } from './RollbackListParts';

export interface RollbackDocDrawerProps {
  app: string;
  jobId: string;
  kind?: RollbackDocKind;
  /** The doc's S3 key; the drawer is open while set. */
  docKey?: string;
  onClose: () => void;
}

const TITLES: Record<RollbackDocKind, string> = { case: 'Case', report: 'Breach report' };

const RollbackDocDrawer: React.FC<RollbackDocDrawerProps> = ({ app, jobId, kind, docKey, onClose }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { wsStatus, rollbackDocs, fetchRollbackDoc } = useDeploymentData();
  const state = docKey ? rollbackDocs.get(docKey) : undefined;
  const [rawOpen, setRawOpen] = useState(false);

  // Keyed on the state, not the click, so a request a dropped connection lost
  // (the hook forgets it) is sent again on reconnect.
  useEffect(() => {
    if (wsStatus === 'connected' && kind && docKey && !state) fetchRollbackDoc(app, jobId, kind, docKey);
  }, [wsStatus, app, jobId, kind, docKey, state, fetchRollbackDoc]);

  useEffect(() => setRawOpen(false), [docKey]);

  const doc = state?.doc;
  const frontMatter = Object.entries(doc?.front_matter || {});

  return (
    <Drawer
      anchor="right"
      open={!!docKey}
      onClose={onClose}
      PaperProps={{ sx: { width: 'min(760px, 100vw)', p: 2.5, boxSizing: 'border-box' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: c.ink }}>
            {kind ? TITLES[kind] : ''}
          </Typography>
          <Typography variant="caption" sx={{ ...mono, color: c.inkMuted, overflowWrap: 'anywhere' }}>
            {docKey}
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {(!state || state.loading) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: c.inkMuted }}>
          <CircularProgress size={14} />
          <Typography variant="body2">Loading…</Typography>
        </Box>
      )}
      {state?.error && <Alert severity="error">{state.error}</Alert>}

      {doc && (
        <>
          {frontMatter.length > 0 && (
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                columnGap: 2,
                rowGap: 0.5,
                m: 0,
                p: 1.5,
                borderRadius: `${rounded.md}px`,
                backgroundColor: c.canvasSoft,
                border: `1px solid ${c.hairline}`,
                '& dt': { color: c.inkMuted, fontSize: '0.8rem' },
                '& dd': { m: 0, ...mono, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
              }}
            >
              {frontMatter.map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt>{k}</dt>
                  <dd>{formatMetaValue(v)}</dd>
                </React.Fragment>
              ))}
            </Box>
          )}

          <Box
            sx={{
              mt: 2,
              color: c.ink,
              fontSize: '0.9rem',
              '& h1, & h2, & h3': { fontWeight: 600, mt: 2.5, mb: 1 },
              '& h1': { fontSize: '1.2rem' },
              '& h2': { fontSize: '1.05rem' },
              '& h3': { fontSize: '0.95rem' },
              '& p, & ul, & ol': { my: 1 },
              '& code': {
                ...mono,
                backgroundColor: c.canvasSoft,
                borderRadius: `${rounded.sm}px`,
                px: 0.5,
              },
              '& pre': {
                ...mono,
                p: 1.5,
                overflowX: 'auto',
                backgroundColor: c.canvasSoft,
                border: `1px solid ${c.hairline}`,
                borderRadius: `${rounded.md}px`,
              },
              '& pre code': { p: 0, backgroundColor: 'transparent' },
              '& table': { borderCollapse: 'collapse', my: 1.5, display: 'block', overflowX: 'auto' },
              '& th, & td': { border: `1px solid ${c.hairline}`, px: 1, py: 0.5, textAlign: 'left' },
              '& th': { backgroundColor: c.canvasSoft, fontWeight: 600 },
              '& a': { color: c.primary },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.body || ''}</ReactMarkdown>
          </Box>

          {kind === 'report' && doc.data !== undefined && (
            <Box sx={{ mt: 2 }}>
              <Button size="small" onClick={() => setRawOpen(o => !o)} aria-expanded={rawOpen}>
                {rawOpen ? 'Hide raw data' : 'Raw data'}
              </Button>
              <Collapse in={rawOpen} unmountOnExit>
                <Box
                  component="pre"
                  sx={{
                    ...mono,
                    p: 1.5,
                    mt: 1,
                    overflowX: 'auto',
                    backgroundColor: c.canvasSoft,
                    border: `1px solid ${c.hairline}`,
                    borderRadius: `${rounded.md}px`,
                  }}
                >
                  {JSON.stringify(doc.data, null, 2)}
                </Box>
              </Collapse>
            </Box>
          )}
        </>
      )}
    </Drawer>
  );
};

export default RollbackDocDrawer;
